import { IElement } from "core-integration/src/store/elements/types";
import { getNextNEvents } from "core-integration/src/store/events/reducers";
import { getFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import ElementDetailsBar from "plos/src/components/ElementCard/ElementDetailsBar";
import { ElementIcon } from "plos/src/components/tooltips/ElementIcon";
import { vars } from "plos/src/styles/theme.css";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { getAccessibleFixtureLabels } from "plos/src/utils/fixtures";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isElementFixtureHome } from "../../../../utils/fixtures";
import { difficultyCell, fdrOverlayWrapper } from "./elementOverlayFDR.css";

const maxFixtures = 3;

interface ElementOverlayFDRProps {
  element: IElement;
}

interface OverlayFixture {
  id: number;
  difficulty: number;
  isBlank: boolean;
  label: string;
}

const ElementOverlayFDR = ({ element }: ElementOverlayFDRProps) => {
  const { t } = useTranslation();
  // Every gameweek yields at least one slot (a fixture, or a dash for a blank),
  // so the next maxFixtures gameweeks are always enough to fill the overlay.
  const upcomingEvents = useSelector(getNextNEvents(maxFixtures));
  const fixturesByEvent = useSelector(getFixturesByEvent);
  const teamsById = useSelector(getTeamsById);

  // Show up to maxFixtures upcoming difficulty ratings. Each fixture
  // fills a slot; a blank gameweek fills a slot with a dash placeholder
  const fixtures: OverlayFixture[] = upcomingEvents
    .flatMap((event) => {
      // Find this player's fixtures in this gameweek (usually one, sometimes
      // 2+ in a double gameweek, sometimes none)
      const elementFixtures = (fixturesByEvent[event.id] ?? []).filter(
        (f) => f.team_h === element.team || f.team_a === element.team,
      );

      const accessibleLabels = getAccessibleFixtureLabels(
        element,
        elementFixtures,
        teamsById,
      );

      // No fixture this gameweek, so show a dash for it
      if (elementFixtures.length === 0) {
        return [
          {
            id: -event.id,
            difficulty: 0,
            isBlank: true,
            label: t("fixtures.noFixture", "No fixture"),
          },
        ];
      }

      // Get the fixture difficulty rating for each fixture
      return elementFixtures.map((fixture, index) => {
        const isHome = isElementFixtureHome(element.team, fixture.team_h);
        const difficulty =
          (isHome ? fixture.team_h_difficulty : fixture.team_a_difficulty) ?? 0;
        return {
          id: fixture.id,
          difficulty,
          isBlank: false,
          label: accessibleLabels[index],
        };
      });
    })
    // Only keep the first maxFixtures fixtures
    .slice(0, maxFixtures);

  if (!fixtures.length) {
    return <ElementDetailsBar>-</ElementDetailsBar>;
  }

  return (
    <ElementDetailsBar>
      <div className={fdrOverlayWrapper}>
        <span className={visuallyHidden}>
          {t(
            "pitchControls.pitchOverlay.fdrLabel",
            "Difficulty of upcoming fixtures:",
          )}
        </span>
        {fixtures.map((fixture) => {
          const rating =
            fixture.difficulty as keyof typeof vars.colors.difficulties;

          return (
            <div key={fixture.id} className={difficultyCell[rating]}>
              <ElementIcon label={fixture.label}>
                {fixture.isBlank ? "-" : rating}
                <span className={visuallyHidden}>,</span>
              </ElementIcon>
            </div>
          );
        })}
      </div>
    </ElementDetailsBar>
  );
};

export default ElementOverlayFDR;
